import { z } from 'zod';
import { mioContextSchema, mioMessageSchema } from './mio.js';
/** Identity of the Workshop attempt whose IDE the server may observe. References only: file
 * contents never travel through the browser or the persisted turn context. */
export const mioIdeContextSchema = z
    .object({
    kind: z.literal('workshop'),
    workspaceId: z.uuid(),
    attemptId: z.uuid(),
    activityId: z.uuid(),
    generation: z.number().int().positive(),
})
    .strict();
const modelValue = z.union([z.string().max(500), z.number().finite(), z.boolean(), z.null()]);
export const mioLearningModelSchema = z
    .object({
    blockId: z.string().min(1).max(300),
    model: z.string().min(1).max(100),
    state: z
        .object({
        scenario: z.string().max(500).optional(),
        step: z.number().int().nonnegative().optional(),
        inputs: z
            .record(z.string().max(200), modelValue)
            .refine((value) => Object.keys(value).length <= 12),
        state: z
            .record(z.string().max(200), modelValue)
            .refine((value) => Object.keys(value).length <= 12),
    })
        .strict()
        .refine((value) => JSON.stringify(value).length <= 3000),
    actions: z
        .array(z.object({
        id: z.string().min(1).max(100),
        label: z.string().max(200),
        kind: z.enum(['button', 'select', 'input']),
        disabled: z.boolean(),
        value: z.string().max(500).optional(),
        options: z
            .array(z.object({ value: z.string().max(500), label: z.string().max(200) }))
            .max(20)
            .optional(),
    }))
        .max(20),
})
    .strict();
/** Browser observations are context, never an authorization source. */
export const mioLiveContextSchema = mioContextSchema.extend({
    tabId: z.string().min(1).max(100),
    revision: z.number().int().nonnegative(),
    observedAt: z.iso.datetime(),
    activityId: z.uuid().nullable().optional(),
    lessonVersion: z.string().max(100).nullable().optional(),
    sectionId: z.string().max(300).nullable().optional(),
    sectionTitle: z.string().max(500).nullable().optional(),
    blockId: z.string().max(300).nullable().optional(),
    traceStep: z.number().int().min(0).max(500).nullable().optional(),
    learningModel: mioLearningModelSchema.nullable().optional(),
    selection: z.string().max(4000).optional(),
    visibleText: z.string().max(6000).optional(),
    scrollPercent: z.number().min(0).max(100).optional(),
    ideContext: mioIdeContextSchema.nullable().optional(),
});
export const mioSourceSchema = z.object({
    id: z.string(),
    title: z.string(),
    url: z.string(),
    kind: z.enum(['curriculum', 'external']),
    activityId: z.string().optional(),
    anchor: z.string().optional(),
    accessedAt: z.string().optional(),
});
const artifactBase = {
    id: z.string(),
    caption: z.string().max(1000),
    sources: z.array(mioSourceSchema).default([]),
};
export const mioArtifactSchema = z.discriminatedUnion('type', [
    z.object({ ...artifactBase, type: z.literal('diagram'), mermaid: z.string().max(20000) }),
    z.object({
        ...artifactBase,
        type: z.literal('code'),
        title: z.string().max(500).optional(),
        language: z.string().max(40),
        code: z.string().max(30000),
    }),
    z.object({
        ...artifactBase,
        type: z.literal('table'),
        columns: z.array(z.string()).max(12),
        rows: z.array(z.array(z.string())).max(100),
    }),
]);
const actionBase = {
    id: z.string(),
    tabId: z.string(),
    contextRevision: z.number().int().nonnegative(),
};
export const mioAgentActionSchema = z.discriminatedUnion('type', [
    z.object({
        ...actionBase,
        type: z.literal('open_lesson'),
        activityId: z.uuid(),
        anchor: z.string().max(300).optional(),
    }),
    z.object({ ...actionBase, type: z.literal('open_workshop'), activityId: z.uuid() }),
    z.object({ ...actionBase, type: z.literal('scroll_to_section'), anchor: z.string().max(300) }),
    z.object({
        ...actionBase,
        type: z.literal('highlight'),
        anchor: z.string().max(300),
        text: z.string().max(4000).optional(),
    }),
    z.object({
        ...actionBase,
        type: z.literal('select_trace_step'),
        blockId: z.string().max(300),
        step: z.number().int().min(0).max(500),
    }),
    z.object({ ...actionBase, type: z.literal('show_artifact'), artifactId: z.string() }),
    z.object({
        ...actionBase,
        type: z.literal('interact_model'),
        blockId: z.string().min(1).max(300),
        actionId: z.string().min(1).max(100),
        value: z.string().max(500).optional(),
    }),
]);
export const mioAgentMessageSchema = mioMessageSchema.extend({
    turnId: z.string().optional(),
    modality: z.enum(['chat', 'voice']).default('chat'),
    /** `queued` exists only in the browser: a message typed while an earlier turn is still running. */
    status: z.enum(['streaming', 'complete', 'interrupted', 'failed', 'queued']).default('complete'),
    modelId: z.string().optional(),
    sources: z.array(mioSourceSchema).default([]),
    artifacts: z.array(mioArtifactSchema).default([]),
});
export const mioMemorySchema = z.object({
    id: z.string(),
    kind: z.enum(['preference', 'concept', 'question', 'episode']),
    text: z.string().max(4000),
    evidenceMessageIds: z.array(z.string()).default([]),
    learnerEdited: z.boolean().default(false),
    updatedAt: z.string(),
});
export const mioLearnerProfileSchema = z.object({
    background: z.string().max(2000).default(''),
    goals: z.string().max(2000).default(''),
    exampleDomain: z.string().max(1000).default(''),
    verbosity: z.enum(['concise', 'balanced', 'detailed']).default('balanced'),
    pace: z.enum(['slow', 'natural', 'fast']).default('natural'),
    quiet: z.boolean().default(false),
    voiceId: z.string().max(100).nullable().default(null),
});
export const mioAgentStateSchema = z.object({
    threadId: z.string(),
    messages: z.array(mioAgentMessageSchema),
    memory: z.array(mioMemorySchema),
    profile: mioLearnerProfileSchema,
    lastEventId: z.string(),
    capabilities: z.object({
        chat: z.boolean(),
        voice: z.boolean(),
        research: z.boolean(),
        voiceConfigured: z.boolean(),
    }),
});
export const mioAgentEventSchema = z.object({
    id: z.string(),
    threadId: z.string(),
    turnId: z.string().nullable().optional(),
    type: z.enum([
        'turn.started',
        'text.delta',
        'speech.ready',
        'speech.queued',
        'message.completed',
        'artifact.created',
        'action.requested',
        'turn.completed',
        'turn.failed',
        'turn.cancelled',
        'turn.progress',
        'memory.updated',
        'context.updated',
        'action.acknowledged',
        'state.reset',
    ]),
    payload: z.record(z.string(), z.unknown()),
    createdAt: z.string(),
});
export const mioAgentTurnRequestSchema = z.object({
    requestId: z.uuid(),
    threadId: z.uuid(),
    message: z.string().trim().min(1).max(12000),
    modality: z.enum(['chat', 'voice']).default('chat'),
    // Input method and response delivery differ when a learner types during a call.
    responseMode: z.enum(['text', 'voice']).optional(),
    voiceSessionId: z.uuid().optional(),
    context: mioLiveContextSchema,
});
export const mioAgentTurnResponseSchema = z.object({ turnId: z.string() });
export const mioMutationResultSchema = z.object({ ok: z.boolean() });
export const mioActionAckSchema = z.object({
    threadId: z.uuid(),
    turnId: z.uuid(),
    tabId: z.string().max(100),
    contextRevision: z.number().int().nonnegative(),
    status: z.enum(['rendered', 'rejected', 'failed']),
    error: z.string().max(500).optional(),
});
export const mioVoiceSessionSchema = z.object({
    sessionId: z.string(),
    token: z.string(),
    voiceId: z.string(),
    modelId: z.string(),
    expiresAt: z.string().optional(),
    audioTransport: z.literal('server').optional(),
});
/** Live PCM is ephemeral: it is never replayed with the durable conversation events. */
export const mioVoiceAudioEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('audio.connected') }),
    z.object({
        type: z.literal('audio.chunk'),
        turnId: z.uuid(),
        sequence: z.number().int().nonnegative(),
        pcm: z
            .string()
            .min(1)
            .max(262_144)
            .regex(/^[A-Za-z0-9+/]+={0,2}$/),
        sampleRate: z.literal(24_000),
    }),
    z.object({
        type: z.literal('audio.end'),
        turnId: z.uuid(),
        sequence: z.number().int().nonnegative(),
    }),
    z.object({
        type: z.literal('audio.error'),
        turnId: z.uuid(),
        message: z.string().max(500),
    }),
]);
export const mioVoiceCatalogSchema = z.object({
    voices: z.array(z.object({
        id: z.string(),
        name: z.string(),
        category: z.string(),
    })),
});
export const mioContextUpdateSchema = z.object({
    threadId: z.uuid(),
    context: mioLiveContextSchema,
});
export const mioVoiceSessionRequestSchema = z.object({
    threadId: z.uuid(),
    voiceId: z
        .string()
        .regex(/^[a-zA-Z0-9_-]{1,100}$/)
        .optional(),
});
export const mioSpeechRequestSchema = z.object({
    sessionId: z.uuid(),
    turnId: z.uuid(),
    text: z.string().trim().min(1).max(3000),
});
/**
 * Appended by the API when an answer stops at its output or tool budget. The panel
 * recognises the exact wording to offer one-tap continuation, so both sides share it.
 */
export const MIO_RESPONSE_LIMIT_NOTICE = 'I reached the response limit here. Ask me to continue from this point.';
/** The learner message the panel sends when they accept that offer. */
export const MIO_CONTINUE_REQUEST = 'Continue from where you stopped.';
export function endsWithMioResponseLimit(text) {
    return text.trimEnd().endsWith(MIO_RESPONSE_LIMIT_NOTICE);
}
/**
 * Where a turn is while its text is still held back. The API publishes these as
 * `turn.progress` events so the panel can say what Mio is doing instead of a bare spinner.
 */
export const MIO_TURN_PHASES = ['drafting', 'working', 'checking', 'repairing'];
export const mioTurnPhaseSchema = z.enum(MIO_TURN_PHASES);
/**
 * A withheld answer always starts with this sentence; the API appends the specific gap.
 * The panel recognises it to offer a retry, and the API keeps such turns out of later
 * model history because the chat model otherwise imitates the refusal.
 */
export const MIO_WITHHELD_PREFIX = 'I have held back my draft answer because I could not confirm it against the lesson and your files.';
/** The fixed wording stored by releases before 2026-10-13; still recognised in old conversations. */
export const MIO_LEGACY_UNVERIFIED_ANSWER = 'I could not verify a reliable answer from the available evidence. I have not run or changed your code. Please narrow this to one claim or the relevant source/output so I can check it without guessing.';
export function isMioWithheldAnswer(text) {
    const trimmed = text.trim();
    return trimmed.startsWith(MIO_WITHHELD_PREFIX) || trimmed === MIO_LEGACY_UNVERIFIED_ANSWER;
}
/** Appended when the checked parts of an answer are published without the parts that failed. */
export function mioPartialAnswerNote(omitted) {
    return `I left out ${omitted} ${omitted === 1 ? 'statement' : 'statements'} I could not confirm from the lesson and your files.`;
}
/** Appended when every statement checked out but the auditor judged the question only partly answered. */
export const MIO_INCOMPLETE_ANSWER_NOTE = 'This may not answer your question in full; tell me which part is missing.';
const partialNotePattern = /(?:^|\n\n)(?:I left out \d+ statements? I could not confirm from the lesson and your files\.|This may not answer your question in full; tell me which part is missing\.)\s*$/;
/** The note is a platform remark, not teaching; it is removed before text re-enters model history. */
export function stripMioPartialAnswerNote(text) {
    // A partial answer the auditor also found incomplete carries both notes.
    let stripped = text;
    for (let pass = 0; pass < 2; pass++)
        stripped = stripped.replace(partialNotePattern, '').trimEnd();
    return stripped;
}
/** Same cheap content identity in browser and API; this is not a security hash. */
export function lessonContentVersion(body) {
    let hash = 0x811c9dc5;
    for (let index = 0; index < body.length; index++) {
        hash ^= body.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
}
